import { FormEvent, ReactNode, useEffect, useRef } from "react"
import { t } from "../../i18n"

export function CwField({
  id,
  label,
  error,
  helper,
  children,
}: {
  id: string
  label: string
  error?: string
  helper?: string
  children: ReactNode
}) {
  return (
    <div>
      <label htmlFor={id} className="block font-semibold text-primary-dark mb-1">
        {label}
      </label>
      {children}
      {helper ? <p className="mt-1 text-sm text-gray-600">{helper}</p> : null}
      {error ? (
        <p className="mt-1 text-sm text-red-600" role="alert">{error}</p>
      ) : null}
    </div>
  )
}

export const cwInputClassName =
  "w-full rounded-xl border border-gray-200 bg-secondary-extra-light px-4 py-3 outline-none focus:border-primary"

export default function CwFormModal({
  isOpen,
  title,
  submitLabel,
  submitting = false,
  formError,
  onClose,
  onSubmit,
  children,
}: {
  isOpen: boolean
  title: string
  submitLabel: string
  submitting?: boolean
  formError?: string
  onClose: () => void
  onSubmit: (event: FormEvent) => void
  children: ReactNode
}) {
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) onClose()
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [isOpen, onClose, submitting])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label={t("common.close")}
        className="absolute inset-0 bg-black/50"
        onClick={() => {
          if (!submitting) onClose()
        }}
        disabled={submitting}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cw-form-title"
          className="relative w-full max-w-lg rounded-2xl bg-white shadow-xl p-6 md:p-8 font-source max-h-[90vh] overflow-y-auto"
        >
          <h2 id="cw-form-title" className="font-oswald text-3xl font-bold text-primary-dark">
            {title}
          </h2>
          <form className="mt-6 space-y-5" onSubmit={onSubmit}>
            {children}
            <div className="flex flex-col items-end gap-3 pt-2">
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="rounded-full border border-gray-300 bg-white px-5 py-2 font-semibold text-primary-dark disabled:opacity-60"
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-full bg-primary px-5 py-2 font-semibold text-white disabled:opacity-60"
                >
                  {submitting ? t("common.saving") : submitLabel}
                </button>
              </div>
              {formError ? (
                <p className="text-sm text-red-600" role="alert">{formError}</p>
              ) : null}
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
